import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingPortal,
  offset,
  shift,
  size as sizeMiddleware,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
  useTransitionStatus,
  useTypeahead,
  type Placement,
} from '@floating-ui/react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { cx } from '../utils.ts';
import './form.css';
import './overlay.css';

export interface SelectOption<V extends string = string> {
  value: V;
  label: ReactNode;
  textValue?: string;
  icon?: ReactNode;
  description?: ReactNode;
  disabled?: boolean;
}

export interface SelectProps<V extends string = string> {
  value: V | null | undefined;
  onChange: (value: V) => void;
  options: SelectOption<V>[];
  placeholder?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'ghost';
  disabled?: boolean;
  className?: string;
  placement?: Placement;
  /** Minimum panel width (defaults to trigger width). */
  minWidth?: number;
  renderValue?: (option: SelectOption<V>) => ReactNode;
  'aria-label'?: string;
  id?: string;
}

export function Select<V extends string = string>({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  size = 'md',
  variant = 'default',
  disabled,
  className,
  placement = 'bottom-start',
  minWidth,
  renderValue,
  id,
  ...aria
}: SelectProps<V>) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const selectedIndex = options.findIndex((o) => o.value === value);
  const listRef = useRef<(HTMLElement | null)[]>([]);
  const labels = options.map((o) => o.textValue ?? (typeof o.label === 'string' ? o.label : null));

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    transform: false,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(6),
      flip({ padding: 10 }),
      shift({ padding: 10 }),
      sizeMiddleware({
        padding: 10,
        apply({ rects, availableHeight, elements }) {
          elements.floating.style.minWidth = `${Math.max(minWidth ?? 0, rects.reference.width)}px`;
          elements.floating.style.maxHeight = `${Math.max(160, Math.min(360, availableHeight))}px`;
        },
      }),
    ],
  });
  const click = useClick(context, { enabled: !disabled });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'listbox' });
  const nav = useListNavigation(context, {
    listRef,
    activeIndex,
    selectedIndex: selectedIndex >= 0 ? selectedIndex : null,
    onNavigate: setActiveIndex,
    loop: true,
  });
  const typeahead = useTypeahead(context, {
    listRef: { current: labels },
    activeIndex,
    selectedIndex: selectedIndex >= 0 ? selectedIndex : null,
    onMatch: open ? setActiveIndex : (i) => options[i] && !options[i]!.disabled && onChange(options[i]!.value),
  });
  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([click, dismiss, role, nav, typeahead]);
  const { isMounted, status } = useTransitionStatus(context, { duration: { open: 150, close: 100 } });
  const current = selectedIndex >= 0 ? options[selectedIndex] : undefined;

  return (
    <>
      <button
        type="button"
        id={id}
        ref={refs.setReference}
        className={cx('g-select', className)}
        data-size={size}
        data-variant={variant}
        data-placeholder={!current || undefined}
        disabled={disabled}
        aria-label={aria['aria-label']}
        {...getReferenceProps()}
      >
        {current?.icon && <span className="g-select__icon">{current.icon}</span>}
        <span className="g-select__value">{current ? (renderValue ? renderValue(current) : current.label) : placeholder}</span>
        <ChevronsUpDown size={14} className="g-select__chevron" />
      </button>
      {isMounted && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false} initialFocus={0} order={['floating', 'content']}>
            <div
              ref={refs.setFloating}
              className="g-popover g-menu"
              data-status={status}
              data-padding="none"
              style={floatingStyles}
              {...getFloatingProps()}
            >
              {options.map((opt, i) => (
                <button
                  key={opt.value}
                  type="button"
                  role="option"
                  aria-selected={i === selectedIndex}
                  className="g-menu__item"
                  data-active={i === activeIndex || undefined}
                  disabled={opt.disabled}
                  tabIndex={i === activeIndex ? 0 : -1}
                  ref={(node) => {
                    listRef.current[i] = node;
                  }}
                  {...getItemProps({
                    onClick() {
                      onChange(opt.value);
                      setOpen(false);
                    },
                  })}
                >
                  {opt.icon && <span className="g-menu__icon">{opt.icon}</span>}
                  <span className="g-menu__text">
                    {opt.label}
                    {opt.description && <span className="g-menu__desc">{opt.description}</span>}
                  </span>
                  <span className="g-menu__check g-menu__check--end">{i === selectedIndex && <Check size={14} />}</span>
                </button>
              ))}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
}
